/**
 * A game in progress, as a document four phones can write to at once.
 *
 * The whole design turns on one decision: **nothing here stores a total.**
 * Life, poison and commander damage are all derived by replaying append-only
 * logs, whose turn it is is the last entry in another, and who is out of the
 * game is a third.
 *
 * That is what makes a shared game possible without conflict resolution. Two
 * phones adjusting the same seat is the case that breaks a document of totals —
 * last write wins, and two hits of three become one hit of three. Two phones
 * appending to a log is not a conflict at all: the union is the answer, in any
 * order, and every device that has seen the same entries computes the same
 * totals. The counter was already built this way for the damage log's sake,
 * which is the only reason this feature is small.
 *
 * The corollary is that a device offline in a basement can keep playing and
 * merge cleanly an hour later, because appends commute.
 */

import type { Commentary } from './commentary.js';
import type { MatchEntry } from './types.js';

/**
 * How many chairs a table can have.
 *
 * Here rather than in the API because the draft has to cap the table by how
 * many decks are physically present, and it is the host's phone that has to
 * say so before a table is opened at all.
 */
export const MIN_SEATS = 2;
export const MAX_SEATS = 4;

export const STARTING_LIFE = 40;
/** A player is dead once one opponent's commander has dealt this much. */
export const LETHAL_COMMANDER_DAMAGE = 21;

/*
 * Which commander dealt it, for a deck with two.
 *
 * Commander damage is per source by rule: twenty-one from one commander kills
 * and twenty from each of two partners does not. So a pilotId is not enough to
 * key it - a partner pair is two commanders sharing a seat, and pooling them
 * would kill somebody who was never dead.
 *
 * A pilotId with a mark on the end rather than a nested record, because the
 * flat shape is already on disk: every match logged so far keys this by plain
 * pilotId, and a plain pilotId is exactly what "their first commander" means.
 * Old games read correctly with no migration, and a deck without a partner
 * never produces the second form at all. The mark cannot collide with a UUID.
 */
const PARTNER_MARK = '#partner';

export const commanderKey = (pilotId: string, partner = false): string =>
  partner ? `${pilotId}${PARTNER_MARK}` : pilotId;

/** Whose commander this was, whichever of their two it is. */
export const pilotOfCommander = (key: string): string =>
  key.endsWith(PARTNER_MARK) ? key.slice(0, -PARTNER_MARK.length) : key;

export const isPartnerCommander = (key: string): boolean => key.endsWith(PARTNER_MARK);
/** Ten poison counters, from any source, and life stops mattering. */
export const LETHAL_POISON = 10;

/**
 * How long after a change an opposite-direction one still reads as fixing it.
 *
 * Long, on purpose. Realising that you took nine off the wrong player, or that
 * it was seven and not nine, takes about as long as it takes to look up from
 * the phone — and the alternative is a life log that claims somebody was healed
 * every time a thumb slipped.
 */
export const AMEND_WINDOW_MS = 8000;

/**
 * One change somebody made.
 *
 * Carries its own id so the same change arriving twice — an optimistic local
 * copy meeting the server's copy of itself — is recognised as one change rather
 * than counted twice. Without it every reconnect would double somebody's damage.
 */
export interface LiveChange {
  id: string;
  /** Which seat it happened to. */
  seat: number;
  delta: number;
  /**
   * Where this came from, as a pilotId. Two logs use it, for related reasons.
   *
   * On the commander log it is which commander dealt the damage, as a
   * `commanderKey` — a pilotId for a single commander, and a marked pilotId for
   * the second half of a partner pair. Commander damage is per-source by rule —
   * twenty-one from one commander kills and twenty from each of three does not
   * — so it cannot be pooled the way life and poison are, and two partners are
   * two sources sharing a seat.
   *
   * On the out log it is who knocked this seat out, which the table confirms
   * rather than the app inferring: the counter knows a life total reached zero
   * and has no idea whose spell took it there. Null means nobody was credited,
   * which is a real answer — a concede, or a board wipe that killed its own
   * caster — and not a missing one.
   */
  source?: string | null;
  turn: number;
  /**
   * Which individual turn this landed in, as a count of passes so far.
   *
   * `turn` is the round, which is one number for the whole table; this is the
   * turn inside it. Stamped when the change begins rather than when it settles,
   * so a burst of taps that outlives the turn it started in is still credited
   * where it happened.
   *
   * Absent on changes recorded before the counter tracked it. Anything reading
   * it has to cope with that, because a live game outlives the code that
   * started it.
   */
  step?: number;
  at: number;
  /**
   * The change this one corrects, when somebody has overshot and come back.
   *
   * Nine down and two back up is a seven-point swing — one stroke, corrected —
   * and not a hit followed by a heal. A burst of taps already collapses into a
   * single change; this is for the correction that arrives after the burst has
   * closed, which is most of them, because noticing you overshot takes longer
   * than a second and a half.
   *
   * Recorded as a pointer rather than by editing the original, because the
   * original may already be on three other phones. Totals are unaffected either
   * way — the log sums — so this only changes the story the log tells.
   */
  amends?: string;
  /**
   * Whoever had priority when it happened, as a pilotId.
   *
   * A guess, not a fact: a board wipe on your own turn kills you too. Good
   * enough to ask questions of a season's worth of games, not good enough to
   * put on screen as truth, which is why it never appears in the UI.
   */
  by: string | null;
}

/** One pass of the turn: who it went to, and which round that was. */
export interface TurnChange {
  id: string;
  seat: number;
  /** Rounds, not individual turns — "turn nine" means nine for everyone. */
  turn: number;
  at: number;
  /**
   * The pass this entry takes back, when it is a correction rather than a pass.
   *
   * A turn passed early is the one mistake a table makes that it cannot argue
   * its way out of: the damage that comes next lands in the wrong turn, and
   * whoever was meant to untap has already lost the window. So the turn can go
   * backwards — by appending, like everything else here, because two phones
   * write this log and truncating it would be a race with somebody's next tap.
   *
   * `seat` and `turn` on a correction are still filled in with where the table
   * lands, even though `foldTurns` throws the entry away before reading them.
   * A phone running an older build reads the last entry and nothing else, and
   * that phone should follow the table rather than argue with it.
   */
  undoes?: string;
}

/** A seat, which is a chair rather than a state. Everything about it is derived. */
export interface LiveSeat {
  /** Null while the chair is empty and waiting for somebody to claim it. */
  pilotId: string | null;
  deckId: string | null;
}

/**
 * `done` is not the same as gone.
 *
 * A finished game stays on the server for a moment so that every phone gets to
 * see the result, not just the one that pressed Confirm. Deleting it the
 * instant the match was logged meant three people watched the game vanish and
 * had to go and find the answer in the history.
 */
export type LivePhase = 'lobby' | 'drafting' | 'playing' | 'done';

/**
 * How the table was filled.
 *
 * Absent on every game logged before the draft existed, and read through
 * `isDraft` rather than compared directly, so an old document in somebody's
 * cache keeps meaning what it meant.
 */
export type LiveMode = 'standard' | 'draft';

/**
 * One thing somebody did in the draft.
 *
 * A log, like everything else here, for the same reason: two phones appending
 * commute, and the alternative — a list of remaining decks that each device
 * rewrites — is the case that loses a ban.
 */
export interface DraftAction {
  id: string;
  kind: 'ban' | 'pick';
  /** Whose chair acted. Seats are identities and never move. */
  seat: number;
  deckId: string;
  at: number;
}

export interface LiveDraft {
  /** What the host said was physically on the table tonight. */
  pool: string[];
  /**
   * The subset dealt for this game, fixed when the turn order was rolled.
   *
   * Drawn once by the server rather than by each phone, for the same reason
   * the starting seat is: an outcome every device computes separately is an
   * outcome two devices can disagree about.
   */
  dealt: string[];
  /** Bans, then picks. Order is the log's order. */
  actions: DraftAction[];
}

export interface LiveGame {
  id: string;
  phase: LivePhase;
  /** Whoever opened it. Only they can start it, end it, or name the winner. */
  hostPilotId: string;
  createdAt: number;
  startedAt: number | null;
  seats: LiveSeat[];
  /** Life. */
  log: LiveChange[];
  poisonLog: LiveChange[];
  commanderLog: LiveChange[];
  /**
   * Players the table has declared out, and anyone put back in.
   *
   * Being out is not always a life total. People concede, or scoop to a combo
   * they cannot answer, or are simply done — and the counter should not have to
   * pretend somebody on fifteen life is still playing because the arithmetic
   * says so. A log rather than a flag, so it merges like everything else and
   * carries the moment it happened, which is what the finishing order is built
   * from.
   */
  outLog: LiveChange[];
  /**
   * Where the turn has been, one entry per pass.
   *
   * A count would be smaller, and was, until turns had to skip players who are
   * out. Whose turn it is stopped being a function of how many passes have
   * happened the moment it also depended on who was alive at each of them — so
   * each pass records the seat it landed on and the round it landed in, and
   * reading the current turn is reading the last entry.
   *
   * Still append-only, so it is still safe for two phones at once, and it has
   * the happy side effect that rearranging the table cannot disturb a turn that
   * has already been taken: these name seats, and seat numbers never move.
   */
  turnLog: TurnChange[];
  /** Rolled by the server at kick-off, so every phone animates to one answer. */
  startingSeat: number;
  /**
   * Where each seat sits round the table, as seat indices in clockwise order.
   *
   * The reason this exists rather than the seats being shuffled directly: every
   * change ever recorded points at a seat by index, so moving a seat would mean
   * rewriting the whole log — a read-modify-write across the one part of the
   * document four phones are appending to. An arrangement kept beside the seats
   * costs one array and touches nothing that has already happened.
   *
   * So a seat index is an identity and never moves. This is the seating plan.
   */
  order: number[];
  /** The odds the table was shown, frozen when the game started. */
  predicted: Record<string, number>;
  /** The match this game became, once somebody confirmed the winner. */
  matchId?: string | null;
  /** Absent on games opened before the draft existed; those are standard. */
  mode?: LiveMode;
  /** Only on a draft. */
  draft?: LiveDraft;
  /**
   * The peanut gallery, if anybody has asked for it yet.
   *
   * Kept on the game rather than derived per phone so that four people watching
   * one table read the same jokes at the same moment. Worth nothing to the
   * league and safe to drop entirely: see `commentary.ts`.
   */
  commentary?: Commentary;
  /**
   * A straight read on the matchup, written once when the table is set.
   *
   * The one piece of generated text in the app that is not a joke. It sits
   * under the odds and says what the numbers mean in a sentence or two, which
   * is the thing the breakdown table underneath was being asked to do and could
   * not: a column of rating differences tells you the size of an edge and
   * nothing at all about where it comes from.
   */
  analysis?: { text: string; at: number };
  updatedAt: number;
}

export function isDraft(game: Pick<LiveGame, 'mode'>): boolean {
  return game.mode === 'draft';
}

/**
 * How many chairs a pool of this size can seat.
 *
 * Two decks a player, because that is the smallest draft that is a draft: each
 * player bans one and picks one, and with any fewer somebody is handed a deck
 * rather than choosing it. Four decks seat two, eight seat four.
 */
export function maxSeatsForPool(poolSize: number): number {
  return Math.min(MAX_SEATS, Math.floor(poolSize / 2));
}

/**
 * How many decks to deal to a table of this size. All of them.
 *
 * It used to deal two a player and one over, drawing that many at random out of
 * a deeper pool, so that the second game of the night started from somewhere
 * different. The trouble is that the number does not move with the pool: at two
 * players it deals five whether you tick five decks or fifteen, so ticking more
 * bought variety between games and never a wider choice inside one. A table
 * that had put eight decks on the table and been shown five read that, quite
 * reasonably, as three decks having gone missing.
 *
 * So the pool is the deal. What is on the table is what is on the table, which
 * is the thing the pool sheet asks for in the first place.
 *
 * `players` is kept in the signature: how many decks a table needs is still a
 * question about the table, and `maxSeatsForPool` is still the other half of
 * it - two decks a player, or the last seat is handed one rather than choosing.
 */
export function draftDealCount(_players: number, poolSize: number): number {
  return poolSize;
}

/**
 * The order seats act in, starting from whoever the wheel landed on.
 *
 * Reads the seating plan rather than seat numbers, so it follows people round
 * the table as they actually sit rather than the order they happened to claim
 * chairs in.
 */
export function draftOrder(game: LiveGame): number[] {
  const plan = orderOf(game).filter((i) => game.seats[i]?.pilotId);
  const at = plan.indexOf(game.startingSeat);
  // A starting seat outside the plan means the seat emptied; start at the top
  // rather than returning nothing.
  if (at < 0) return plan;
  return [...plan.slice(at), ...plan.slice(0, at)];
}

export interface DraftView {
  stage: 'ban' | 'pick' | 'done';
  /** The seat that has to act, or null once the draft is finished. */
  seat: number | null;
  /** Still on the table: neither banned nor taken. */
  available: string[];
  banned: string[];
  /** What each seat ended up with, keyed by seat index. */
  taken: Record<number, string>;
  /** How many acts remain in the whole draft. */
  remaining: number;
}

/**
 * Where the draft has got to, derived from the log alone.
 *
 * Both halves run in turn order, from whoever the wheel landed on. It used to
 * snake — bans down the order and picks back up it, so that whoever banned
 * first chose last and neither seat was simply the one to have. The table
 * asked for the straight order instead: it is the order everything else in the
 * app already runs in, and a draft nobody can predict the order of is a draft
 * people ask about every time.
 *
 * That does hand the first seat both the first ban and the first pick. What
 * keeps it fair is the wheel — the order is rolled fresh for every game, so
 * the seat that gets it is not the same seat twice running.
 */
export function draftView(game: LiveGame): DraftView | null {
  if (!game.draft) return null;
  const order = draftOrder(game);
  const n = order.length;
  const actions = game.draft.actions;
  const bans = actions.filter((a) => a.kind === 'ban');
  const picks = actions.filter((a) => a.kind === 'pick');

  const banned = bans.map((a) => a.deckId);
  const taken: Record<number, string> = {};
  for (const p of picks) taken[p.seat] = p.deckId;
  const gone = new Set([...banned, ...picks.map((p) => p.deckId)]);
  const available = game.draft.dealt.filter((id) => !gone.has(id));

  const stage = bans.length < n ? 'ban' : picks.length < n ? 'pick' : 'done';
  const seat =
    stage === 'ban'
      ? (order[bans.length] ?? null)
      : stage === 'pick'
        ? (order[picks.length] ?? null)
        : null;

  return { stage, seat, available, banned, taken, remaining: n * 2 - actions.length };
}

export function newLiveGame(
  id: string,
  hostPilotId: string,
  size: number,
  draft?: { pool: string[] },
): LiveGame {
  return {
    id,
    phase: 'lobby',
    hostPilotId,
    mode: draft ? 'draft' : 'standard',
    ...(draft ? { draft: { pool: draft.pool, dealt: [], actions: [] } } : {}),
    createdAt: Date.now(),
    startedAt: null,
    seats: Array.from({ length: size }, () => ({ pilotId: null, deckId: null })),
    log: [],
    poisonLog: [],
    commanderLog: [],
    outLog: [],
    turnLog: [],
    startingSeat: 0,
    order: Array.from({ length: size }, (_, i) => i),
    predicted: {},
    updatedAt: Date.now(),
  };
}

/**
 * The seating plan, defaulted for a game that predates having one.
 *
 * A live game outlives the code that started it — it sits on the server while
 * the app updates underneath it — so anything read out of the document has to
 * cope with the field not being there.
 */
export function orderOf(game: LiveGame): number[] {
  const n = game.seats.length;
  const stored = game.order;
  const valid =
    Array.isArray(stored) &&
    stored.length === n &&
    new Set(stored).size === n &&
    stored.every((i) => Number.isInteger(i) && i >= 0 && i < n);
  return valid ? stored : Array.from({ length: n }, (_, i) => i);
}

/**
 * Move a seat round the table, keeping everything that has happened intact.
 *
 * Only the arrangement changes. Nothing is rewritten: no life change moves, no
 * damage is reassigned, and no turn already taken is disturbed — every log in
 * the document names seats by index, and seat indices never move.
 *
 * Whoever's turn it is keeps it for free, because the current turn is the last
 * entry in the turn log and that entry names a seat.
 *
 * Turn order from here follows the new arrangement, which is the point:
 * Commander turn order is physical, so if the app had the table wrong then it
 * had the turn order wrong too, and fixing one fixes the other.
 */
export function reorderSeats(game: LiveGame, from: number, to: number): number[] {
  const order = orderOf(game);
  const n = order.length;
  if (from === to || from < 0 || to < 0 || from >= n || to >= n) return order;

  const next = [...order];
  const [moved] = next.splice(from, 1);
  next.splice(to, 0, moved!);
  return next;
}

/**
 * The passes that still count, with the taken-back ones removed.
 *
 * Both halves of a correction go: the pass that should not have happened, and
 * the entry that says so. What is left is the sequence of turns the table
 * would have had if nobody had mistapped, which is what "whose turn is it"
 * has to be read from.
 *
 * A correction whose target is missing is dropped rather than kept, which is
 * the opposite of what `foldAmendments` does with an orphan. The two are
 * different questions: an amendment carries a number that is worth something on
 * its own, and an undo carries nothing at all.
 */
export function foldTurns(log: readonly TurnChange[]): TurnChange[] {
  const undone = new Set<string>();
  for (const t of log) if (t.undoes) undone.add(t.undoes);
  return log.filter((t) => !t.undoes && !undone.has(t.id));
}

/** Whose turn it is, as a stable seat index. */
export function activeSeatOf(game: LiveGame): number {
  const last = foldTurns(game.turnLog ?? []).at(-1);
  return last ? last.seat : game.startingSeat;
}

/** Which round the table is on. */
export function turnOf(game: LiveGame): number {
  return foldTurns(game.turnLog ?? []).at(-1)?.turn ?? 1;
}

/**
 * Where the turn lands if the table takes the last pass back, or null if there
 * is nothing to take back.
 *
 * Null on the opening turn, and null again once every pass has been undone —
 * the game cannot reverse past its own first turn, so a table that keeps
 * tapping back eventually runs out rather than wrapping round.
 *
 * The round comes back with the seat, so a pass that ticked over into turn
 * nine leaves the table on turn eight again.
 */
export function stepBack(
  game: LiveGame,
): { undoes: string; seat: number; turn: number } | null {
  const kept = foldTurns(game.turnLog ?? []);
  const last = kept.at(-1);
  if (!last) return null;
  const before = kept.at(-2);
  return {
    undoes: last.id,
    seat: before?.seat ?? game.startingSeat ?? 0,
    turn: before?.turn ?? 1,
  };
}

/**
 * Where the turn goes next, skipping anybody who is out.
 *
 * Computed against the table as it stands rather than replayed, because who was
 * alive changes with every pass and the answer is only ever needed for the next
 * one. A round ticks over when play wraps back round the table, which stays
 * true however many people have been knocked out of it.
 */
export function nextTurn(view: LiveView): TurnChange {
  const order = view.order;
  const n = order.length;
  const from = Math.max(0, order.indexOf(view.activeSeat));
  const stamp = { id: '', at: 0 };

  // Nobody left to pass to. A game that is over stays where it is rather than
  // spinning round an empty table looking for somebody to play.
  if (!order.some((seat) => !isOut(view.seats[seat]!))) {
    return { ...stamp, seat: view.activeSeat, turn: view.turn };
  }

  for (let step = 1; step <= n; step++) {
    const seat = order[(from + step) % n]!;
    if (isOut(view.seats[seat]!)) continue;
    return { ...stamp, seat, turn: view.turn + (from + step >= n ? 1 : 0) };
  }
  return { ...stamp, seat: view.activeSeat, turn: view.turn };
}

// ---- Derivation -------------------------------------------------------------

export interface DerivedSeat {
  index: number;
  pilotId: string | null;
  deckId: string | null;
  life: number;
  poison: number;
  /** Commander damage taken, keyed by the pilotId whose commander dealt it. */
  commanderDamage: Record<string, number>;
  /**
   * The table said they are out.
   *
   * The only thing that actually removes somebody from the game. Kept apart
   * from the arithmetic because it is a different kind of fact: life reaching
   * zero is something the app worked out, and this is something a person
   * decided.
   */
  conceded: boolean;
  /**
   * When the numbers first said they should be dead, and had not come back.
   *
   * A prompt, not a verdict. Zero life is not the end of a turn in Commander —
   * there is a response to be had, a trigger to point at, or it was a thumb on
   * the wrong pad — so this asks the table rather than telling it.
   */
  downAt: number | null;
  downTurn: number | null;
  /**
   * When this seat left the game, or null while they are still in it.
   *
   * The moment the table said so, falling back to the moment the numbers said
   * so for anybody nobody ever got round to confirming. That fallback is only
   * for the finishing order: a game does not stop being lost because nobody
   * pressed the button, and the order people went out in is the whole
   * difference between second and last.
   *
   * Read out of the logs rather than stamped, because a stamp is a write and
   * the point of this document is that the hot path has none.
   */
  outAt: number | null;
  outTurn: number | null;
  /**
   * What they were on when they left, which separates two seats that went out
   * in the same round.
   *
   * Not the same as `life`, which is where the total ended up: a board wipe
   * after somebody is out still lands on their pad, and the question here is
   * what they had when it stopped mattering.
   */
  lifeAtOut: number | null;
  /**
   * Which individual turn they went out on, or null on a game recorded before
   * the counter tracked turns separately from rounds.
   *
   * The round is too coarse to order eliminations by — three people conceding
   * across one round of a four-player game went out at three different moments
   * — and the clock is too fine, because two players killed by the same combo
   * are separated only by which thumb was quicker. The individual turn is the
   * unit where "at the same time" is actually true.
   */
  outStep: number | null;
  /**
   * Who the table said knocked them out, or null if nobody was credited.
   *
   * Only ever set by a person answering the question. There is no inference
   * behind it — whoever had priority is a guess good enough for a season's
   * worth of statistics and nowhere near good enough to pay points for.
   */
  outBy: string | null;
}

export interface LiveView {
  /**
   * Every seat, in stable index order — `seats[i].index === i`.
   *
   * Indexed rather than arranged, so anything holding a seat number can look it
   * up directly. Use `order` to draw the table.
   */
  seats: DerivedSeat[];
  /** The seating plan: seat indices, clockwise from wherever the layout starts. */
  order: number[];
  /** Rounds, not individual turns — "we're on turn nine" means nine for everyone. */
  turn: number;
  /** Whose turn it is, as a stable seat index. */
  activeSeat: number;
}

/** The seats as they sit round the table, for drawing one. */
export function seatsInOrder(view: LiveView): DerivedSeat[] {
  return view.order.map((i) => view.seats[i]!).filter(Boolean);
}

/**
 * Total order over changes from every device.
 *
 * By wall clock, then by id. The tiebreak is not cosmetic: two phones can stamp
 * the same millisecond, and without a deterministic second key they would sort
 * differently on each device and disagree about who went out first.
 */
function chronological(a: LiveChange, b: LiveChange): number {
  return a.at - b.at || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0);
}

/** Merge two views of the same log, keeping one copy of anything in both. */
export function mergeChanges(a: readonly LiveChange[], b: readonly LiveChange[]): LiveChange[] {
  const byId = new Map<string, LiveChange>();
  for (const change of a) byId.set(change.id, change);
  for (const change of b) byId.set(change.id, change);
  return [...byId.values()].sort(chronological);
}

/** Everything the table can see, replayed from everything the table has done. */
export function deriveView(game: LiveGame): LiveView {
  const seats: DerivedSeat[] = game.seats.map((s, index) => ({
    index,
    pilotId: s.pilotId,
    deckId: s.deckId,
    life: STARTING_LIFE,
    poison: 0,
    commanderDamage: {},
    conceded: false,
    downAt: null,
    downTurn: null,
    outAt: null,
    outTurn: null,
    lifeAtOut: null,
    outStep: null,
    outBy: null,
  }));

  /*
   * All three logs replayed as one stream, in the order things actually
   * happened. They have to be interleaved rather than run one after another:
   * somebody who drops to two life and is then finished by a commander went out
   * at the commander damage, and running the life log to completion first would
   * put the moment in the wrong place — which is the moment that decides the
   * finishing order.
   */
  const stream = [
    ...game.log.map((c) => ({ ...c, kind: 'life' as const })),
    ...game.poisonLog.map((c) => ({ ...c, kind: 'poison' as const })),
    ...game.commanderLog.map((c) => ({ ...c, kind: 'commander' as const })),
    ...(game.outLog ?? []).map((c) => ({ ...c, kind: 'out' as const })),
  ].sort(chronological);

  /** When the table declared each seat out, kept aside until the end. */
  const declared: ({
    at: number;
    turn: number;
    step: number | null;
    life: number;
    by: string | null;
  } | null)[] = seats.map(() => null);
  /** What each seat was on when the numbers first said they were dead. */
  const whenDown: ({ life: number; step: number | null } | null)[] = seats.map(() => null);

  for (const change of stream) {
    const seat = seats[change.seat];
    if (!seat) continue;

    if (change.kind === 'out') {
      // Positive puts somebody out, negative puts them back in.
      seat.conceded = change.delta > 0;
      declared[change.seat] =
        change.delta > 0
          ? {
              at: change.at,
              turn: change.turn,
              step: stepOf(change),
              // What they had when the table called it, which is the number
              // that separates two people who went out on the same turn.
              life: seat.life,
              by: change.source ?? null,
            }
          : null;
    } else if (change.kind === 'life') {
      seat.life += change.delta;
    } else if (change.kind === 'poison') {
      seat.poison = Math.max(0, seat.poison + change.delta);
    } else {
      const from = change.source ?? '';
      seat.commanderDamage[from] = Math.max(0, (seat.commanderDamage[from] ?? 0) + change.delta);
    }

    /*
     * Evaluated after every change rather than at the end, because the end
     * knows who is dead and not the order they got there.
     *
     * Cleared if they come back above the line, because a mis-tap should not
     * bury somebody who is visibly still playing — and nor should a turn where
     * they were briefly at zero and then gained it all back.
     */
    const down = isDown(seat);
    if (down && seat.downAt === null) {
      seat.downAt = change.at;
      seat.downTurn = change.turn;
      whenDown[seat.index] = { life: seat.life, step: stepOf(change) };
    } else if (!down && seat.downAt !== null) {
      seat.downAt = null;
      seat.downTurn = null;
      whenDown[seat.index] = null;
    }
  }

  for (const seat of seats) {
    const said = declared[seat.index];
    const fell = whenDown[seat.index] ?? null;
    seat.outAt = said ? said.at : seat.downAt;
    seat.outTurn = said ? said.turn : seat.downTurn;
    seat.lifeAtOut = said ? said.life : (fell?.life ?? null);
    seat.outStep = said ? said.step : (fell?.step ?? null);
    // Only ever what somebody confirmed. A seat the table never got round to
    // marking has no killer, however obvious it looked at the time.
    seat.outBy = said ? said.by : null;
  }

  return {
    seats,
    order: orderOf(game),
    turn: turnOf(game),
    activeSeat: activeSeatOf(game),
  };
}

/**
 * Out of the game, which only the table can decide.
 *
 * Deliberately not "life is zero". Nothing about a life total reaching zero
 * ends a Commander player's game on its own: there is a response to be had, a
 * trigger to point at, a replacement effect, or somebody's thumb landed on the
 * wrong pad. Every one of those is common enough that a counter which decides
 * for you is a counter people stop trusting.
 *
 * So the numbers ask (`isDown`) and the table answers. This is the answer, and
 * it is what stops the turn coming round to somebody.
 */
export function isOut(seat: Pick<DerivedSeat, 'conceded'>): boolean {
  return seat.conceded === true;
}

/**
 * Whether the numbers say this seat should be dead.
 *
 * The prompt, not the verdict — what makes the app ask. Poison and commander
 * damage are in here for the same reason life is: they are lethal by rule, and
 * by rule is not the same as agreed at the table.
 */
export function isDown(
  seat: Pick<DerivedSeat, 'life' | 'poison' | 'commanderDamage'>,
): boolean {
  return (
    seat.life <= 0 ||
    seat.poison >= LETHAL_POISON ||
    Object.values(seat.commanderDamage).some((d) => d >= LETHAL_COMMANDER_DAMAGE)
  );
}

/**
 * Where everyone finished, from the order they went out.
 *
 * The winner is first. Behind them, anyone still alive when the game ended ties
 * — they were not eliminated and the game gave no way to separate them, so the
 * rating should not pretend otherwise. Then the eliminated, latest first,
 * because outlasting somebody is the one thing the counter actually witnessed.
 *
 * Ranks skip the places a tie consumed, so two players tied second are 2, 2, 4.
 * That is not cosmetic: the engine reads these to decide each pair, and a tie
 * has to leave the place below it empty or the arithmetic stops matching what
 * the screen says.
 *
 * Within one turn the clock is not the answer. Two players killed by the same
 * combo are separated only by which of two thumbs was quicker, which is a fact
 * about phones rather than about the game — so inside a turn it is life
 * remaining that decides. Somebody finished from fifteen by a commander
 * outlasted somebody who had already been chipped to nothing, whoever the table
 * happened to mark out first.
 *
 * The turn, not the round. Three people conceding across one round of a
 * four-player game went out at three genuinely different moments, and calling
 * that a three-way tie would throw away most of what the counter watched.
 */
export function standingOf(view: LiveView, winnerPilotId: string): Record<string, number> {
  const standing: Record<string, number> = { [winnerPilotId]: 1 };

  /*
   * Ordered by when each seat left, which is not the same question as whether
   * the table ever pressed the button. A game that ended with two people on
   * zero life and nobody confirming it still had an order, and the ratings
   * depend on it.
   *
   * Infinity puts "never went out" at the front of both keys without a special
   * case, so survivors sort ahead of everyone eliminated and tie with each
   * other — the game gave no way to separate them and the rating should not
   * pretend otherwise.
   */
  const rest = view.seats
    .filter((s) => s.pilotId && s.pilotId !== winnerPilotId)
    .map((s) => ({
      pilotId: s.pilotId!,
      step: s.outAt === null ? Infinity : s.outStep,
      at: s.outAt ?? Infinity,
      life: s.lifeAtOut ?? 0,
    }));

  /**
   * Later is better, and inside one turn more life is better.
   *
   * A game recorded before the counter tracked individual turns has no step to
   * compare, and falls back to the clock — which is exactly how those games
   * were always ordered, so nothing already logged changes meaning.
   */
  function finishedAbove(a: (typeof rest)[number], b: (typeof rest)[number]): number {
    if (a.step === null || b.step === null) return b.at - a.at;
    if (a.step !== b.step) return b.step - a.step;
    return b.life - a.life;
  }

  rest.sort(finishedAbove);

  let place = 2;
  for (let i = 0; i < rest.length; ) {
    let group = 1;
    while (i + group < rest.length && finishedAbove(rest[i + group]!, rest[i]!) === 0) group++;
    for (let j = 0; j < group; j++) standing[rest[i + j]!.pilotId] = place;
    place += group;
    i += group;
  }
  return standing;
}

/**
 * Who the table said knocked whom out, in the shape the match records it.
 *
 * Keyed by victim, because each player is knocked out exactly once and may take
 * several people with them. Seats become pilotIds here for the same reason the
 * damage log converts: a seat stops existing when the game does.
 *
 * Only confirmed credits appear. A seat that went out with nobody named — the
 * common case for a concede — is left out entirely rather than attributed to
 * whoever happened to hold priority.
 */
export function takedownsOf(view: LiveView): Record<string, string> {
  const takedowns: Record<string, string> = {};
  for (const seat of view.seats) {
    if (!seat.pilotId || !seat.outBy) continue;
    // Nobody takes themselves down; the engine refuses it and the UI does not
    // offer it, so this only catches a document written by an older client.
    if (seat.outBy === seat.pilotId) continue;
    takedowns[seat.pilotId] = seat.outBy;
  }
  return takedowns;
}

/**
 * Every turn the game actually had, in order, for the match to keep.
 *
 * The first entry is the opening turn, which no pass produced — `turnLog`
 * records where the turn *went*, so the seat that started never appears in it.
 * Prepending it is what makes the result indexable: `turnStepsOf(game)[step]`
 * is the turn a change stamped with that step happened in, with no off-by-one
 * to remember at every call site.
 *
 * Seats rather than people, matching `seatedEntries` — both are indexed by the
 * seat they came from, so a chart can join the two without a lookup table.
 *
 * Deliberately not folded, and this is the one place in the file where that is
 * true. Every life change carries the index it was stamped with, so shortening
 * this array would silently re-point every change recorded after a taken-back
 * pass at somebody else's turn. A turn the table reversed still happened on the
 * screen, and the damage dealt during it belongs where it was dealt.
 */
export function turnStepsOf(game: LiveGame): { seat: number; turn: number }[] {
  return [
    { seat: game.startingSeat ?? 0, turn: 1 },
    ...(game.turnLog ?? []).map((t) => ({ seat: t.seat, turn: t.turn })),
  ];
}

/**
 * Which turn a change belongs to, for a game recorded before steps existed.
 *
 * Falls back to the round, which is what the chart used to draw everything at.
 * Not a guess at the missing precision — just the coarser answer, honestly.
 */
export function stepOf(change: Pick<LiveChange, 'step'>): number | null {
  return typeof change.step === 'number' && change.step >= 0 ? change.step : null;
}

// ---- Reading the document ---------------------------------------------------

/** Everyone who has actually sat down. A lobby is mostly empty chairs. */
export function seatedEntries(game: LiveGame): MatchEntry[] {
  return game.seats
    .filter((s): s is { pilotId: string; deckId: string } => Boolean(s.pilotId && s.deckId))
    .map((s) => ({ pilotId: s.pilotId, deckId: s.deckId }));
}

export function seatOf(game: LiveGame, pilotId: string | undefined): number {
  if (!pilotId) return -1;
  return game.seats.findIndex((s) => s.pilotId === pilotId);
}

/** Two players and a deck each, and nobody sharing either. */
export function readyToStart(game: LiveGame): boolean {
  /*
   * A draft lobby has nobody holding a deck yet, and that is the point — the
   * decks are what the draft is about to hand out. So it asks only for the
   * people, plus a pool deep enough to deal them two each.
   */
  if (isDraft(game)) {
    const seated = game.seats.filter((s) => s.pilotId);
    if (seated.length < MIN_SEATS) return false;
    if (new Set(seated.map((s) => s.pilotId)).size !== seated.length) return false;
    return (game.draft?.pool.length ?? 0) >= seated.length * 2;
  }

  const entries = seatedEntries(game);
  if (entries.length < 2) return false;
  const pilots = new Set(entries.map((e) => e.pilotId));
  const decks = new Set(entries.map((e) => e.deckId));
  return pilots.size === entries.length && decks.size === entries.length;
}

/**
 * Corrections folded back into what they corrected.
 *
 * The totals never needed this — a log that sums gets the right answer whether
 * the nine and the plus-two are one entry or two. What needs it is everything
 * that reads the log as a story: the biggest hit anybody took, how much life
 * was gained, the shape of the line on the chart.
 */
export function foldAmendments(changes: readonly LiveChange[]): LiveChange[] {
  const byId = new Map<string, LiveChange>();
  const order: string[] = [];
  for (const c of changes) {
    if (!c.amends) {
      byId.set(c.id, { ...c });
      order.push(c.id);
    }
  }
  for (const c of changes) {
    if (!c.amends) continue;
    const target = byId.get(c.amends);
    if (target) {
      target.delta += c.delta;
    } else {
      // The change it corrects is not here — a log sliced, or an amendment that
      // outlived its target. Standing alone is better than disappearing.
      byId.set(c.id, { ...c });
      order.push(c.id);
    }
  }
  // A correction that cancelled a change outright leaves nothing to say.
  return order.map((id) => byId.get(id)!).filter((c) => c.delta !== 0);
}

/**
 * The changes, in the shape the finished match records them.
 *
 * The live document is keyed by seat, because seats are what a table has; a
 * match is keyed by pilotId, because a seat stops existing the moment the game
 * does. Converted once here rather than at each of the several places that read
 * a finished game.
 */
export function damageLogOf(
  game: LiveGame,
  changes: readonly LiveChange[],
): { to: string; from: string | null; delta: number; turn: number; step?: number }[] {
  return foldAmendments(changes)
    .map((c) => {
      const step = stepOf(c);
      return {
        to: game.seats[c.seat]?.pilotId ?? '',
        from: c.by,
        delta: c.delta,
        turn: c.turn,
        // Omitted rather than nulled when a change predates steps: the field is
        // optional on the match and DynamoDB would keep an explicit null.
        ...(step === null ? {} : { step }),
      };
    })
    .filter((c) => c.to);
}
